import { useCallback, useContext, useEffect, useState } from 'react';
import {
    Box,
    Card,
    CardActions,
    CardContent,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    Grid,
    IconButton,
    LinearProgress,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import MoneyOffIcon from '@mui/icons-material/MoneyOff';
import { showModal } from 'react-redux-modal-provider';
import { toast } from 'react-toastify';
import { SocketContext } from '../../context/SocketContext';
import { ButtonBasic } from '../../components';
import NotifyBajaDAService from '../../services/notifyBajaDA.service';
import ModalRechazoDAGroup from '../../modals/administrador/ModalRechazoDAGroup';
import ModalConfirmIniciarProceso from '../../modals/administrador/ModalConfirmIniciarProceso';
import ModalBajaDebitoRechazado from '../../modals/softland/ModalBajaDebitoRechazado';

const ESTADO_COLORS = {
    PENDIENTE: '#1976d2',
    EN_PROGRESO: '#1565c0',
    COMPLETADO: '#2e7d32',
    VACIO: '#9e9e9e',
};

const ESTADO_LABELS = {
    PENDIENTE: 'Pendiente',
    EN_PROGRESO: 'En progreso',
    COMPLETADO: 'Completado',
    VACIO: 'Vacío',
};

const formatFecha = (fecha) => {
    if (!fecha) return '-';
    try {
        const d = new Date(fecha);
        return d.toLocaleString('es-AR', {
            day: '2-digit', month: '2-digit', year: 'numeric',
            hour: '2-digit', minute: '2-digit'
        });
    } catch (e) {
        return '-';
    }
};

const RechazoDAScreen = () => {
    const theme = useTheme();
    const isDarkMode = theme.palette.mode === 'dark';
    const { sockets } = useContext(SocketContext);
    const [groups, setGroups] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [confirmEliminar, setConfirmEliminar] = useState({ open: false, keyGroup: null });

    const fetchGroups = useCallback(async () => {
        setIsLoading(true);
        try {
            const resp = await NotifyBajaDAService.getGroups();
            console.log(resp)
            setGroups(resp?.grupos || []);
        } catch (err) {
            const msg = err?.response?.data?.msg || 'No se pudieron cargar los grupos.';
            toast.error(msg);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        document.title = 'Rechazo Débito Automático';
        fetchGroups();
    }, [fetchGroups]);

    useEffect(() => {
        if (!sockets) return;
        sockets.emit('joinRechazoDaRoom');
        sockets.on('rechazoDaRefresh', fetchGroups);
        return () => {
            sockets.emit('leaveRechazoDaRoom');
            sockets.off('rechazoDaRefresh', fetchGroups);
        };
    }, [sockets, fetchGroups]);

    const onOpenDetalle = (keyGroup) => {
        showModal(ModalRechazoDAGroup, { keyGroup, onRefresh: fetchGroups });
    };

    const onIniciar = (group) => {
        showModal(ModalConfirmIniciarProceso, {
            keyGroup: group.keyGroup,
            total: group.pendientes,
            onConfirm: fetchGroups,
        });
    };

    const onEliminarGrupo = (keyGroup) => {
        setConfirmEliminar({ open: true, keyGroup });
    };

    const onConfirmEliminar = async () => {
        const { keyGroup } = confirmEliminar;
        setConfirmEliminar({ open: false, keyGroup: null });
        try {
            await NotifyBajaDAService.deleteGroup(keyGroup);
            toast.success('Grupo eliminado correctamente');
            fetchGroups();
        } catch (err) {
            const msg = err?.response?.data?.msg || 'No se pudo eliminar el grupo.';
            toast.error(msg);
        }
    };

    const onNuevo = () => {
        const hasProcesoActivo = groups.some(g => g.estadoGeneral === 'EN_PROGRESO');
        showModal(ModalBajaDebitoRechazado, { hasProcesoActivo });
    };

    return (
        <>
        <Box sx={{ width: '100%', height: '100%', overflow: 'auto', bgcolor: isDarkMode ? '#1a2332' : '#f5f5f5' }} className='side'>
            <Grid container p={3}>
                <Grid item xs={12} sm={8} display='flex' alignItems='center' gap={1}>
                    <MoneyOffIcon sx={{ color: isDarkMode ? '#90caf9' : '#1976d2', fontSize: 36 }} />
                    <Typography sx={{ color: isDarkMode ? '#e0e0e0' : '#000', fontWeight: 'bold' }} variant='h4'>
                        Rechazo Débito Automático
                    </Typography>
                </Grid>
                <Grid item xs={12} sm={4} sx={{ display: 'flex', justifyContent: 'end', alignItems: 'center', gap: 1 }}>
                    <ButtonBasic
                        textButton='Nuevo'
                        color='primary'
                        onClick={onNuevo}
                        sx={{ height: 36 }}
                    />
                    <Tooltip title='Actualizar'>
                        <IconButton onClick={fetchGroups} sx={{ color: isDarkMode ? '#fff' : '#555' }}>
                            <RefreshIcon />
                        </IconButton>
                    </Tooltip>
                </Grid>

                <Grid item xs={12} mt={2}>
                    {isLoading && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                            <CircularProgress sx={{ color: isDarkMode ? '#90caf9' : '#1976d2' }} />
                        </Box>
                    )}

                    {!isLoading && groups.length === 0 && (
                        <Box sx={{
                            textAlign: 'center',
                            py: 8,
                            color: isDarkMode ? '#9aa8be' : '#666',
                            border: `2px dashed ${isDarkMode ? '#3d4a62' : '#bdbdbd'}`,
                            borderRadius: 3,
                            background: isDarkMode ? '#253147' : '#fff',
                        }}>
                            <MoneyOffIcon sx={{ fontSize: 64, color: isDarkMode ? '#3d4a62' : '#bdbdbd' }} />
                            <Typography sx={{ mt: 2, fontWeight: 'bold' }}>
                                No hay grupos registrados
                            </Typography>
                            <Typography sx={{ fontSize: 13, mt: 1 }}>
                                Los grupos se crean desde la acción "Baja Débito Automático" del contacto.
                            </Typography>
                        </Box>
                    )}

                    {!isLoading && groups.length > 0 && (
                        <Grid container spacing={2}>
                            {groups.map((g) => {
                                const estadoColor = ESTADO_COLORS[g.estadoGeneral] || '#9e9e9e';
                                const estadoLabel = ESTADO_LABELS[g.estadoGeneral] || g.estadoGeneral;
                                const porcentaje = g.total > 0 ? Math.round((g.completados / g.total) * 100) : 0;
                                const isCompletado = g.estadoGeneral === 'COMPLETADO';
                                return (
                                    <Grid item xs={12} sm={6} md={4} lg={3} key={g.keyGroup}>
                                        <Card sx={{
                                            background: isDarkMode ? '#253147' : '#fff',
                                            borderTop: `4px solid ${estadoColor}`,
                                            borderRadius: 2,
                                            height: '100%',
                                            display: 'flex',
                                            flexDirection: 'column',
                                        }}>
                                            <CardContent sx={{ pb: 1 }}>
                                                <Stack direction='row' alignItems='center' spacing={1} sx={{ mb: 1 }}>
                                                    <Chip
                                                        label={g.keyGroup}
                                                        size='small'
                                                        sx={{
                                                            fontFamily: 'monospace',
                                                            fontWeight: 'bold',
                                                            fontSize: 14,
                                                            background: isDarkMode ? '#1565c0' : '#1976d2',
                                                            color: '#fff',
                                                            letterSpacing: 1,
                                                        }}
                                                    />
                                                    <Box sx={{ flex: 1 }} />
                                                    {isCompletado && (
                                                        <CheckCircleIcon sx={{ color: isDarkMode ? '#4cceac' : '#2e7d32' }} />
                                                    )}
                                                </Stack>

                                                <Typography sx={{ fontSize: 12, color: isDarkMode ? '#9aa8be' : '#666' }}>
                                                    Creado: {formatFecha(g.createdAt)}
                                                </Typography>

                                                <Divider sx={{ my: 1.5, background: isDarkMode ? '#3d4a62' : '#e0e0e0' }} />

                                                <Stack direction='row' spacing={2} justifyContent='space-around' sx={{ mb: 1.5 }}>
                                                    <Box sx={{ textAlign: 'center' }}>
                                                        <Typography sx={{ fontSize: 22, fontWeight: 'bold', color: isDarkMode ? '#fff' : '#111' }}>
                                                            {g.total}
                                                        </Typography>
                                                        <Typography sx={{ fontSize: 11, color: isDarkMode ? '#9aa8be' : '#666' }}>
                                                            Total
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ textAlign: 'center' }}>
                                                        <Typography sx={{ fontSize: 22, fontWeight: 'bold', color: isDarkMode ? '#4cceac' : '#2e7d32' }}>
                                                            {g.completados}
                                                        </Typography>
                                                        <Typography sx={{ fontSize: 11, color: isDarkMode ? '#9aa8be' : '#666' }}>
                                                            Enviados
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ textAlign: 'center' }}>
                                                        <Typography sx={{ fontSize: 22, fontWeight: 'bold', color: isDarkMode ? '#90caf9' : '#1976d2' }}>
                                                            {g.pendientes}
                                                        </Typography>
                                                        <Typography sx={{ fontSize: 11, color: isDarkMode ? '#9aa8be' : '#666' }}>
                                                            Pendientes
                                                        </Typography>
                                                    </Box>
                                                </Stack>

                                                <LinearProgress
                                                    variant='determinate'
                                                    value={porcentaje}
                                                    sx={{
                                                        height: 6,
                                                        borderRadius: 3,
                                                        background: isDarkMode ? '#3d4a62' : '#e0e0e0',
                                                        '& .MuiLinearProgress-bar': {
                                                            background: estadoColor,
                                                        },
                                                    }}
                                                />

                                                <Stack direction='row' alignItems='center' spacing={1} sx={{ mt: 1.5 }}>
                                                    <Chip
                                                        label={estadoLabel}
                                                        size='small'
                                                        sx={{
                                                            background: estadoColor,
                                                            color: '#fff',
                                                            fontWeight: 'bold',
                                                            fontSize: 11,
                                                        }}
                                                    />
                                                    <Typography sx={{ fontSize: 11, color: isDarkMode ? '#9aa8be' : '#666' }}>
                                                        {porcentaje}% completado
                                                    </Typography>
                                                </Stack>
                                            </CardContent>

                                            <Box sx={{ flex: 1 }} />

                                            <CardActions sx={{ p: 1.5, pt: 0, justifyContent: 'space-between' }}>
                                                <Stack direction='row' spacing={0.5}>
                                                    <Tooltip title='Ver detalle'>
                                                        <IconButton size='small' onClick={() => onOpenDetalle(g.keyGroup)} sx={{ color: isDarkMode ? '#90caf9' : '#1976d2' }}>
                                                            <VisibilityIcon fontSize='small' />
                                                        </IconButton>
                                                    </Tooltip>
                                                    {!isCompletado && (
                                                        <Tooltip title='Eliminar grupo (solo pendientes)'>
                                                            <IconButton size='small' onClick={() => onEliminarGrupo(g.keyGroup)} sx={{ color: isDarkMode ? '#f87171' : '#d32f2f' }}>
                                                                <DeleteIcon fontSize='small' />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                </Stack>
                                                {!isCompletado && g.pendientes > 0 && (
                                                    <ButtonBasic
                                                        textButton='Iniciar'
                                                        size='small'
                                                        color='primary'
                                                        iconStart={<PlayArrowIcon fontSize='small' />}
                                                        onClick={() => onIniciar(g)}
                                                    />
                                                )}
                                            </CardActions>
                                        </Card>
                                    </Grid>
                                );
                            })}
                        </Grid>
                    )}
                </Grid>
            </Grid>
        </Box>

        <Dialog open={confirmEliminar.open} onClose={() => setConfirmEliminar({ open: false, keyGroup: null })} maxWidth='xs' fullWidth>
            <DialogTitle sx={{ background: isDarkMode ? '#1F2A40' : '#fff', color: isDarkMode ? '#fff' : '#111' }}>
                Eliminar grupo
            </DialogTitle>
            <DialogContent sx={{ background: isDarkMode ? '#1F2A40' : '#fff' }}>
                <DialogContentText sx={{ color: isDarkMode ? '#9aa8be' : '#666' }}>
                    ¿Eliminar todos los registros pendientes del grupo{' '}
                    <strong style={{ fontFamily: 'monospace', color: isDarkMode ? '#90caf9' : '#1976d2' }}>{confirmEliminar.keyGroup}</strong>?<br />
                    Los registros ya enviados no se eliminarán.
                </DialogContentText>
            </DialogContent>
            <DialogActions sx={{ background: isDarkMode ? '#1F2A40' : '#fff', p: 2 }}>
                <ButtonBasic
                    textButton='Cancelar'
                    variant='outlined'
                    color='inherit'
                    onClick={() => setConfirmEliminar({ open: false, keyGroup: null })}
                />
                <ButtonBasic
                    textButton='Eliminar'
                    color='error'
                    onClick={onConfirmEliminar}
                />
            </DialogActions>
        </Dialog>
        </>
    );
};

export default RechazoDAScreen;
